/**
 * ResultStep
 *
 * The "Aha Moment" reveal - dramatic before/after transformation.
 * World-class design with cinematic reveal and refined interactions.
 *
 * Design Philosophy:
 * - Theatrical reveal moment
 * - Intuitive before/after slider with polished interactions
 * - Subtle celebratory details
 * - Clear path forward
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAtomValue } from 'jotai';
import {
  ChevronRight,
  Download,
  Share2,
  Sparkles,
  Clock,
  GripVertical,
} from 'lucide-react';
import { cn } from '../../../lib/cn';
import {
  OnboardingLayout,
  OnboardingStepContainer,
  OnboardingHeading,
  OnboardingButton,
} from '../OnboardingLayout';
import { stagingResultAtom } from '../../../state/onboardingAtoms';
import type { OnboardingStepProps } from '../OnboardingRouter';

const STYLE_NAMES: Record<string, string> = {
  modern: 'Modern',
  contemporary: 'Contemporary',
  minimalist: 'Minimalist',
  scandinavian: 'Scandinavian',
  traditional: 'Traditional',
  luxury: 'Luxury',
  farmhouse: 'Farmhouse',
  industrial: 'Industrial',
  coastal: 'Coastal',
  bohemian: 'Bohemian',
};

const ROOM_NAMES: Record<string, string> = {
  living_room: 'Living Room',
  bedroom: 'Bedroom',
  kitchen: 'Kitchen',
  dining_room: 'Dining Room',
  home_office: 'Home Office',
  bathroom: 'Bathroom',
  other: 'Room',
};

export function ResultStep({ onStepComplete, onboardingData }: OnboardingStepProps) {
  const stagingResult = useAtomValue(stagingResultAtom);
  const [showReveal, setShowReveal] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Trigger reveal animation
  useEffect(() => {
    const timer = setTimeout(() => setShowReveal(true), 600);
    return () => clearTimeout(timer);
  }, []);

  // Track time spent viewing
  useEffect(() => {
    const startTime = Date.now();
    return () => {
      const viewDuration = Math.round((Date.now() - startTime) / 1000);
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'onboarding_result_viewed',
          view_duration_seconds: viewDuration,
          session_id: onboardingData.session_id,
        });
      }
    };
  }, [onboardingData.session_id]);

  // Slider interaction handlers
  const updateSliderPosition = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    updateSliderPosition(e.clientX);
  }, [updateSliderPosition]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    updateSliderPosition(e.clientX);
  }, [isDragging, updateSliderPosition]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setIsDragging(true);
    updateSliderPosition(e.touches[0].clientX);
  }, [updateSliderPosition]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return;
    updateSliderPosition(e.touches[0].clientX);
  }, [isDragging, updateSliderPosition]);

  const handleContinue = async () => {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'onboarding_result_continue',
        session_id: onboardingData.session_id,
      });
    }

    await onStepComplete('result', {
      time_to_first_stage_seconds: stagingResult?.processingTimeMs
        ? Math.round(stagingResult.processingTimeMs / 1000)
        : undefined,
    });
  };

  // Error state
  if (!stagingResult) {
    return (
      <OnboardingLayout showProgress={true} showBack={true}>
        <OnboardingStepContainer centered>
          <div className="text-center">
            <h2 className="font-serif text-2xl text-charcoal mb-4">
              Something went wrong
            </h2>
            <p className="text-sand mb-8">
              We couldn't load your staged result.
            </p>
            <OnboardingButton onClick={() => window.history.back()}>
              Go Back
            </OnboardingButton>
          </div>
        </OnboardingStepContainer>
      </OnboardingLayout>
    );
  }

  const styleName = STYLE_NAMES[stagingResult.style] || stagingResult.style;
  const roomName = ROOM_NAMES[stagingResult.roomType] || 'Room';
  const processingSeconds = stagingResult.processingTimeMs
    ? Math.round(stagingResult.processingTimeMs / 1000)
    : null;

  return (
    <OnboardingLayout showProgress={true} showSkip={true}>
      <OnboardingStepContainer maxWidth="4xl">
        <AnimatePresence mode="wait">
          {!showReveal ? (
            // Pre-reveal state
            <motion.div
              key="loading"
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-20"
            >
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="size-20 bg-olive/10 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <Sparkles className="size-9 text-olive" />
              </motion.div>
              <h2 className="font-serif text-2xl text-charcoal">
                Preparing your transformation
              </h2>
            </motion.div>
          ) : (
            // Reveal state
            <motion.div
              key="result"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              <OnboardingHeading
                eyebrow="Transformation complete"
                title="Your room, reimagined"
                subtitle={`${styleName} staging for your ${roomName.toLowerCase()}`}
              />

              {/* Before/After Comparison Slider */}
              <motion.div
                initial={{ opacity: 0, y: 24, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 0.2, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                ref={containerRef}
                className={cn(
                  'relative rounded-3xl overflow-hidden mb-6 select-none',
                  'shadow-[0_25px_50px_-12px_rgba(0,0,0,0.15)]',
                  'ring-1 ring-black/5',
                  isDragging ? 'cursor-grabbing' : 'cursor-ew-resize'
                )}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleMouseUp}
              >
                {/* Container aspect ratio */}
                <div className="relative aspect-[16/10] sm:aspect-[16/9] w-full bg-stone/10">
                  {/* Original image (full background) - the "Before" */}
                  <img
                    src={stagingResult.originalUrl}
                    alt="Original room"
                    className="absolute inset-0 w-full h-full object-cover"
                    draggable={false}
                  />

                  {/* Staged image (clipped with clip-path) - the "After" */}
                  <div
                    className="absolute inset-0"
                    style={{
                      clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
                      willChange: 'clip-path',
                    }}
                  >
                    <img
                      src={stagingResult.stagedUrl}
                      alt="Staged room"
                      className="absolute inset-0 w-full h-full object-cover"
                      draggable={false}
                    />
                  </div>

                  {/* Slider divider line with gradient glow */}
                  <div
                    className="absolute top-0 bottom-0 w-[3px] pointer-events-none"
                    style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
                  >
                    {/* Outer glow */}
                    <div className="absolute inset-0 w-6 -translate-x-1/2 bg-gradient-to-r from-transparent via-white/30 to-transparent blur-sm" />
                    {/* Core line */}
                    <div className="absolute inset-0 bg-white shadow-[0_0_20px_rgba(255,255,255,0.5)]" />
                  </div>

                  {/* Slider handle */}
                  <motion.div
                    className="absolute top-1/2 pointer-events-none"
                    style={{ left: `${sliderPosition}%` }}
                    animate={{
                      scale: isDragging ? 1.08 : 1,
                      y: '-50%',
                      x: '-50%'
                    }}
                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                  >
                    {/* Outer ring glow */}
                    <div className={cn(
                      'absolute inset-0 rounded-full transition-all duration-300',
                      isDragging
                        ? 'scale-150 bg-olive/20 blur-xl'
                        : 'scale-125 bg-white/10 blur-md'
                    )} />

                    {/* Handle pill */}
                    <div className={cn(
                      'relative size-14 rounded-full flex items-center justify-center',
                      'bg-white shadow-[0_4px_20px_rgba(0,0,0,0.15),0_0_0_1px_rgba(255,255,255,0.8)]',
                      'transition-shadow duration-300',
                      isDragging && 'shadow-[0_8px_30px_rgba(0,0,0,0.2),0_0_0_2px_rgba(255,255,255,1)]'
                    )}>
                      {/* Grip pattern */}
                      <div className="flex items-center gap-0.5">
                        <div className="w-0.5 h-5 rounded-full bg-stone/40" />
                        <div className="w-0.5 h-5 rounded-full bg-stone/40" />
                        <div className="w-0.5 h-5 rounded-full bg-stone/40" />
                      </div>
                    </div>
                  </motion.div>

                  {/* Labels with smart positioning */}
                  <motion.div
                    className="absolute top-4 left-4"
                    animate={{
                      opacity: sliderPosition > 15 ? 1 : 0,
                      x: sliderPosition > 15 ? 0 : -10
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="px-4 py-2 bg-charcoal/80 backdrop-blur-md rounded-full border border-white/10">
                      <span className="text-white text-xs font-sans font-semibold uppercase tracking-widest">
                        Before
                      </span>
                    </div>
                  </motion.div>

                  <motion.div
                    className="absolute top-4 right-4"
                    animate={{
                      opacity: sliderPosition < 85 ? 1 : 0,
                      x: sliderPosition < 85 ? 0 : 10
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="px-4 py-2 bg-olive/90 backdrop-blur-md rounded-full border border-white/20 shadow-lg shadow-olive/20">
                      <span className="text-white text-xs font-sans font-semibold uppercase tracking-widest">
                        After
                      </span>
                    </div>
                  </motion.div>

                  {/* Subtle vignette for depth */}
                  <div className="absolute inset-0 pointer-events-none rounded-3xl shadow-[inset_0_0_100px_rgba(0,0,0,0.1)]" />
                </div>
              </motion.div>

              {/* Drag hint with animated icon */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.4 }}
                className="flex items-center justify-center gap-2 text-sand text-sm font-sans mb-8"
              >
                <motion.div
                  animate={{ x: [-3, 3, -3] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <GripVertical className="size-4" />
                </motion.div>
                <span>Drag to compare</span>
              </motion.div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-wrap justify-center gap-6 mb-10"
              >
                {processingSeconds && (
                  <div className="flex items-center gap-2 text-sand">
                    <Clock className="size-4" />
                    <span className="text-sm font-sans">
                      Staged in {processingSeconds}s
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sand">
                  <Sparkles className="size-4" />
                  <span className="text-sm font-sans">{styleName} style</span>
                </div>
              </motion.div>

              {/* Social proof */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="bg-white/60 border border-stone/20 rounded-2xl p-6 mb-10 text-center max-w-lg mx-auto"
              >
                <p className="text-charcoal font-sans text-sm">
                  <span className="font-semibold">73% of buyers</span> say staging
                  makes it easier to visualize a property as their future home.
                </p>
              </motion.div>

              {/* CTA */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              >
                <OnboardingButton onClick={handleContinue} size="lg">
                  Continue Setup
                  <ChevronRight className="size-5" />
                </OnboardingButton>

                {/* Secondary actions */}
                <div className="flex gap-2">
                  <button
                    disabled
                    className="p-3 rounded-xl border border-stone/30 text-sand opacity-50 cursor-not-allowed"
                    title="Available after setup"
                    aria-label="Download (available after setup)"
                  >
                    <Download className="size-5" />
                  </button>
                  <button
                    disabled
                    className="p-3 rounded-xl border border-stone/30 text-sand opacity-50 cursor-not-allowed"
                    title="Available after setup"
                    aria-label="Share (available after setup)"
                  >
                    <Share2 className="size-5" />
                  </button>
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="text-center text-antique text-xs font-sans mt-4"
              >
                Download and share available after completing setup
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>
      </OnboardingStepContainer>
    </OnboardingLayout>
  );
}
